"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Hyphenated } from "@/utils/Hyphenated";
import { SERVICES as services } from "@/data/services-data";
import NumbersSection from "@/components/ServiceNumbers/NumbersSection";
import ServiceInfo from "@/components/ServiceNumbers/ServiceInfo";
import AllPrivateNumbersList from "@/components/ClientComponents/AllPrivateNumbersList";


interface CountryObj {
  id: number;
  country_name: string;
  country_alpha_code: string;
  country_flag: string;
  country_tel_code: number;
  isActive?: boolean;
}

interface CountryDataEntry {
  country: CountryObj;
  freeNumbers: any[];
  privateNumbers: any[];
  generatedAt: string;
}

interface Props {
  countriesData: Record<string, CountryDataEntry>;
  countries: CountryObj[];
}

/**
 * Helper function to get display name from service slug
 * Example: "tiktok-verification-number" -> "Tiktok Verification"
 */
function getServiceDisplay(serviceValue: string): string | null {
  const matchingService = services.find((s) => s.value === serviceValue);
  return matchingService?.label || null;
}

export default function GeneratorDynamicContent({
  countriesData,
  countries,
}: Props) {
  const [countryParam, setCountryParam] = useState<string | null>(null);
  const [serviceParam, setServiceParam] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryObj | null>(null);
  const [freeNumbers, setFreeNumbers] = useState<any[]>([]);
  const originalTitleRef = useRef<string>("");

  // Read both params from URL
  const getParamsFromUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      country: params.get("country"),
      service: params.get("service"),
    };
  }, []);

  // Inject noindex meta and canonical for ?country=&service= URLs
  const injectMeta = useCallback(
    (country: CountryObj, serviceValue: string) => {
      const serviceDisplay = getServiceDisplay(serviceValue) || "SMS Verification";

      let robotsMeta = document.querySelector(
        'meta[name="robots"]'
      ) as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, follow");

      let canonicalLink = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement | null;
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute(
        "href",
        "https://smss.biz/temporary-phone-number-generator/"
      );

      document.title = `Free ${country.country_name} ${serviceDisplay} Numbers | SMSS.BIZ`;
    },
    []
  );

  const cleanupMeta = useCallback(() => {
    const robotsMeta = document.querySelector(
      'meta[name="robots"][content="noindex, follow"]'
    );
    if (robotsMeta) robotsMeta.remove();

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
    }
  }, []);

  // Sync from URL on mount, on popstate (back/forward), and on custom generatorchange event
  useEffect(() => {
    originalTitleRef.current = document.title;

    const syncFromUrl = () => {
      const { country, service } = getParamsFromUrl();
      setCountryParam(country);
      setServiceParam(service);
    };

    const handleGeneratorChange = (e: Event) => {
      const { country, service } = (e as CustomEvent).detail || {};
      setCountryParam(country || null);
      setServiceParam(service || null);
    };

    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    window.addEventListener("generatorchange", handleGeneratorChange);
    return () => {
      window.removeEventListener("popstate", syncFromUrl);
      window.removeEventListener("generatorchange", handleGeneratorChange);
    };
  }, [getParamsFromUrl]);

  // Load country data when countryParam or serviceParam changes
  useEffect(() => {
    if (!countryParam || !serviceParam) {
      setSelectedCountry(null);
      setFreeNumbers([]);
      cleanupMeta();
      return;
    }

    const data = countriesData[countryParam];
    if (data) {
      setSelectedCountry(data.country);
      setFreeNumbers(data.freeNumbers || []);
    } else {
      setSelectedCountry(null);
      setFreeNumbers([]);
    }
  }, [countryParam, serviceParam, countriesData, cleanupMeta]);

  // Inject/cleanup meta when selection changes
  useEffect(() => {
    if (selectedCountry && serviceParam) {
      injectMeta(selectedCountry, serviceParam);
    }
  }, [selectedCountry, serviceParam, injectMeta]);

  // Only render content when BOTH country and service are selected
  if (!countryParam || !serviceParam || !selectedCountry) {
    return null;
  }

  const serviceDisplay = getServiceDisplay(serviceParam);

  // If the service param doesn't match any known service, don't render
  if (!serviceDisplay) {
    return null;
  }

  const countryName = selectedCountry.country_name;
  const countrySlug = Hyphenated(countryName);

  // Extract platform name (e.g. "Roblox" from "Roblox Verification")
  const platformName =
    serviceDisplay.replace(/\s*(verification|2fa)\s*/gi, "").trim() ||
    serviceDisplay;

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to Verify ${platformName} With a Free Temporary ${countryName} Number`,
    description: `Step-by-step guide to getting a free temporary ${countryName} phone number to verify your ${platformName} account without using your real number.`,
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Choose a free temporary number",
        text: `Pick any available free ${countryName} temporary phone number from the list above, or rent a private number for guaranteed delivery.`,
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: `Enter the number on ${platformName}`,
        text: `Copy the ${countryName} number and paste it into the ${platformName} verification field when prompted for a phone number.`,
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Receive your verification code",
        text: `Return to SMSS.BIZ and wait a few seconds. Your ${platformName} verification code will appear automatically on the number's page in real-time.`,
      },
      {
        "@type": "HowToStep",
        position: 4,
        name: `Complete ${platformName} verification`,
        text: `Enter the received code into ${platformName} to finish verifying your account. The entire process takes under a minute.`,
      },
    ],
  };

  return (
    <div className="py-4 lg:py-14 space-y-6">
      {/* Header */}
      <div className="w-full text-center space-y-4">
        <h2 className="text-gray-700 font-bold text-3xl md:text-4xl">
          {selectedCountry.country_flag} {countryName} {serviceDisplay} Free
          Temporary Numbers
        </h2>
        <p className="font-medium text-gray-500">
          {freeNumbers.length > 0
            ? `${freeNumbers.length} free temp phone numbers available for ${serviceDisplay} in ${countryName}`
            : `Currently no temp phone numbers available for ${serviceDisplay} in ${countryName}`}
        </p>
      </div>

      {/* Numbers Section */}
      <NumbersSection
        title={`Free Temporary Numbers For ${serviceDisplay}`}
        numbers={freeNumbers}
        countryName={countryName}
        viewAllUrl={`/free-temporary-numbers/${countrySlug}`}
        isPrivate={false}
        serviceSpecific={true}
        countryParam={countrySlug}
      />

      {/* Private numbers — always shown: any private number works for any
          service in any country, so there is always something to rent. */}
      <AllPrivateNumbersList
        guaranteeCountry={countryName}
        guaranteeService={serviceDisplay}
      />

      {/* Information Section */}
      <ServiceInfo countryName={countryName} serviceDisplay={serviceDisplay} />

      {/* HowTo Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />
    </div>
  );
}
